import { CompanyMode } from "@repo/app-types/entities/company";
import { isTeammateATeamMember } from "@repo/app-types/entities/teammate";
import {
  PermissionActions,
  PermissionSubjects,
} from "@repo/app-types/entities/teammatePermissions";
import { FeatureFlagKey } from "@repo/app-types/featureFlags/keys";
import { canAccessKnowledgeBaseSync } from "@repo/app-types/helpers/knowledgeBaseSyncAccess";
import { useIsFeatureFlagEnabled } from "../../helpers/featureFlags/hooks.js";
import { useIsEmbeddedInFountain } from "../../helpers/fountain.js";
import { useTeammateAuthorization } from "../../helpers/teammateAuthorizationContext.js";
import { useAppSelector } from "../../hooks/hook.js";
import {
  selectIsFountainCompany,
  selectIsRecruitingModeCompany,
} from "../company/companySlice.js";
import { selectTeammate } from "../teammate/teammateSlice.js";
import type { settingsSections } from "./settingsSections.js";

export type SettingsSectionKey = keyof typeof settingsSections;

interface SettingsSectionAccess {
  /** Whether the section's routes may render (reachable by URL). */
  canAccess: boolean;
  /** Whether the section appears in the settings nav; implies `canAccess`. */
  isInNav: boolean;
}

export interface SettingsSectionsPolicy {
  sections: Record<SettingsSectionKey, SettingsSectionAccess>;
  /**
   * Recruiting companies get the recruiting integrations page (listed in the
   * nav's interview-workflow group); sales performance companies get the
   * sales integrations pages (listed under developers).
   */
  usesRecruitingIntegrations: boolean;
  /** Where the integrations item sits in the nav, if shown at all. */
  integrationsNavGroup: "interviewWorkflow" | "developers" | null;
}

/**
 * The single source of truth for which settings sections a teammate can
 * reach and which of those the nav lists. Some sections are deliberately
 * reachable but unlisted (their nav rules are stricter than their access
 * rules, e.g. Recording without the team performance flag), which is why the
 * two are separate answers — but a listed section is always accessible.
 */
export function useSettingsSectionsPolicy(): SettingsSectionsPolicy {
  const isRecruitingModeCompany = useAppSelector(selectIsRecruitingModeCompany);
  const isFountainCompany = useAppSelector(selectIsFountainCompany);
  const isEmbeddedInFountain = useIsEmbeddedInFountain();

  const teammate = useAppSelector(selectTeammate);
  // Team members can only reach their own profile in settings.
  const isTeamMember = Boolean(teammate && isTeammateATeamMember(teammate));

  const teammateAuthorization = useTeammateAuthorization();
  const canAccessBilling = teammateAuthorization.can(
    PermissionActions.READ,
    PermissionSubjects.BILLING,
  );
  const canAccessUserCustomFields = teammateAuthorization.can(
    PermissionActions.READ,
    PermissionSubjects.USER_CUSTOM_FIELDS,
  );
  const canAccessDevelopers = teammateAuthorization.can(
    PermissionActions.READ,
    PermissionSubjects.DEVELOPERS,
  );
  const canAccessWebhooks = teammateAuthorization.can(
    PermissionActions.READ,
    PermissionSubjects.WEBHOOKS,
  );
  const canAccessCallRecorderSettings = teammateAuthorization.can(
    PermissionActions.READ,
    PermissionSubjects.RECORDING_ASSESSMENT_TEMPLATES,
  );
  const canAccessKnowledgeBases = teammateAuthorization.can(
    PermissionActions.READ,
    PermissionSubjects.KNOWLEDGE_BASES,
  );

  const isKnowledgeBaseEnabled = useIsFeatureFlagEnabled(
    FeatureFlagKey.KnowledgeBase,
  );
  const isVoiceAgentsEnabled = useIsFeatureFlagEnabled(
    FeatureFlagKey.AgentsTab,
  );
  const isRecruitingEnabled = !useIsFeatureFlagEnabled(
    FeatureFlagKey.HideHireTab,
  );
  const isTeamPerformanceTabEnabled = useIsFeatureFlagEnabled(
    FeatureFlagKey.TeamPerformanceTab,
  );
  const isSignalsEnabled = useIsFeatureFlagEnabled(
    FeatureFlagKey.TopicsAndSignals,
  );

  // Show data extraction section if either feature flag is set.
  const isDataExtractionVisible =
    isRecruitingEnabled || isTeamPerformanceTabEnabled;
  const isCallIntelligenceInNav =
    isTeamPerformanceTabEnabled && canAccessCallRecorderSettings;
  const isRecruitingIntegrationsVisible =
    !isEmbeddedInFountain && isRecruitingEnabled;
  const isSalesIntegrationsVisible =
    !isEmbeddedInFountain && !isFountainCompany && !isRecruitingEnabled;

  // `buildAccess(access, inNavWhenAccessible)` makes nav ⊆ access structural:
  // an item can't be listed without being reachable.
  const buildAccess = (
    canAccess: boolean,
    isInNavWhenAccessible = true,
  ): SettingsSectionAccess => ({
    canAccess: !isTeamMember && canAccess,
    isInNav: !isTeamMember && canAccess && isInNavWhenAccessible,
  });

  const integrationsAccess = buildAccess(
    isRecruitingIntegrationsVisible || isSalesIntegrationsVisible,
    isRecruitingIntegrationsVisible
      ? isRecruitingModeCompany && !isFountainCompany
      : true,
  );
  const integrationsNavGroup = !integrationsAccess.isInNav
    ? null
    : isRecruitingIntegrationsVisible
      ? "interviewWorkflow"
      : "developers";

  return {
    sections: {
      profile: { canAccess: true, isInNav: true },
      organization: buildAccess(true),
      teammates: buildAccess(true),
      // The teammates tabs; their own gates live with the tab routes.
      teammateGroups: buildAccess(true, false),
      signIn: buildAccess(true, false),
      billing: buildAccess(canAccessBilling),
      forms: buildAccess(
        isRecruitingModeCompany,
        isRecruitingEnabled && !isEmbeddedInFountain,
      ),
      messageTemplates: buildAccess(
        isRecruitingModeCompany,
        isRecruitingEnabled && !isFountainCompany,
      ),
      integrations: integrationsAccess,
      dataExtraction: buildAccess(
        isRecruitingModeCompany && isDataExtractionVisible,
      ),
      customFields: buildAccess(
        isRecruitingModeCompany && canAccessUserCustomFields,
        !isEmbeddedInFountain,
      ),
      keywordTrackers: buildAccess(!isRecruitingModeCompany),
      recording: buildAccess(isRecruitingModeCompany, isCallIntelligenceInNav),
      playbooks: buildAccess(
        isRecruitingModeCompany && canAccessCallRecorderSettings,
        isTeamPerformanceTabEnabled,
      ),
      knowledgeBases: buildAccess(
        canAccessKnowledgeBaseSync({
          companyMode: isRecruitingModeCompany ? CompanyMode.RECRUITING : null,
          isAgentsEnabled: isVoiceAgentsEnabled,
          isKnowledgeBaseEnabled,
        }) && canAccessKnowledgeBases,
        isCallIntelligenceInNav,
      ),
      signals: buildAccess(
        isRecruitingModeCompany && isSignalsEnabled,
        isCallIntelligenceInNav,
      ),
      webhooks: buildAccess(canAccessWebhooks),
      developers: buildAccess(canAccessDevelopers),
    },
    usesRecruitingIntegrations: isRecruitingIntegrationsVisible,
    integrationsNavGroup,
  };
}
